import React from "react";
import { useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { editorStoreContext } from "../../../store/editorStore";
import {
  CatalogMaterialRenderOverrides,
  CatalogSurfaceReference,
} from "../products/product3DTypes";
import { getBuiltInFurnitureMaterial } from "./builtInFurnitureMaterials";
import {
  resolveCatalogSurfaceRefForRender,
  useBuiltInMaterialSurfaceMappingsRevision,
} from "./builtInMaterialSurfaceMappings";
import { trackFloorplan3DMetric } from "../metrics/floorplan3DMetrics";
import {
  acquireCatalogTextureVariant,
  releaseCatalogTextureVariant,
} from "./textureCache";
import {
  resolveCatalogTextureRepeatScale,
  resolveCatalogTextureRepeatScaleY,
} from "./wallTextureTiling";

type CatalogStandardMaterialProps = {
  attach?: string;
  color: THREE.ColorRepresentation;
  surfaceRef?: CatalogSurfaceReference;
  defaults?: CatalogMaterialRenderOverrides;
  side?: THREE.Side;
  opacity?: number;
  transparent?: boolean;
  depthWrite?: boolean;
  /**
   * Geometry UV set for the albedo map. Walls pass `1` so wallpaper tiles in
   * metres via `uv1` from `applyWallTextureUVs` instead of stretching 0–1.
   */
  uvChannel?: 0 | 1;
  /**
   * Cap downloaded albedo size. Walls / floors pass 512 and force the CDN `w`
   * param so older stored URLs with `w=1024` still downscale.
   */
  maxTextureSize?: number;
  /** When true with maxTextureSize, overwrite an existing larger `w` on the URL. */
  forceTextureWidth?: boolean;
};

/**
 * Resolve the mesh tint for catalog materials.
 *
 * Textured Surfaces persist `color: 0xffffff` so albedo is not multiplied once
 * the map is bound. While the map is loading or missing, prefer the catalog
 * category tint (e.g. oak brown) over that stored white so shapes/stairs do
 * not flash or stick white when switching materials.
 */
export function resolveCatalogStandardMaterialColor(params: {
  map: THREE.Texture | null;
  imageUrl?: string;
  surfaceRefColor?: number;
  slotColor: THREE.ColorRepresentation;
  catalogFallbackColor?: number;
}): THREE.ColorRepresentation {
  const {
    map,
    imageUrl,
    surfaceRefColor,
    slotColor,
    catalogFallbackColor,
  } = params;
  if (imageUrl && !map) {
    return catalogFallbackColor ?? slotColor;
  }
  if (surfaceRefColor !== undefined) {
    return surfaceRefColor;
  }
  return map ? 0xffffff : slotColor;
}

const CatalogStandardMaterial: React.FC<CatalogStandardMaterialProps> = observer(({
  attach,
  color,
  surfaceRef,
  defaults,
  side = THREE.DoubleSide,
  opacity = 1,
  transparent = false,
  depthWrite = true,
  uvChannel = 0,
  maxTextureSize,
  forceTextureWidth = false,
}) => {
  const editorStore = React.useContext(editorStoreContext);
  const { gl, invalidate } = useThree();
  const [map, setMap] = React.useState<THREE.Texture | null>(null);
  const mapRef = React.useRef<THREE.Texture | null>(null);
  const materialRef = React.useRef<THREE.MeshPhysicalMaterial | null>(null);
  const hideTextures = editorStore.hide3DMaterialTextures;
  // Re-resolve when the pack loads so lean surfaceRefs pick up albedo URLs.
  useBuiltInMaterialSurfaceMappingsRevision();
  const resolvedSurfaceRef = resolveCatalogSurfaceRefForRender(surfaceRef);
  const imageUrl = hideTextures ? undefined : resolvedSurfaceRef?.imageUrl;
  const metreSpaceUvs = uvChannel === 1;
  const repeatScale = resolveCatalogTextureRepeatScale({
    repeatScale: resolvedSurfaceRef?.repeatScale ?? surfaceRef?.repeatScale,
    defaultsRepeatScale: defaults?.repeatScale,
    metreSpaceUvs,
  });
  const repeatScaleY = resolveCatalogTextureRepeatScaleY({
    repeatScaleY:
      resolvedSurfaceRef?.repeatScaleY ?? surfaceRef?.repeatScaleY,
    defaultsRepeatScaleY: defaults?.repeatScaleY,
  });
  const rotation =
    resolvedSurfaceRef?.rotation ?? surfaceRef?.rotation ?? defaults?.rotation ?? 0;
  const roughness =
    resolvedSurfaceRef?.roughness ??
    surfaceRef?.roughness ??
    defaults?.roughness ??
    0.78;
  const metalness =
    resolvedSurfaceRef?.metalness ??
    surfaceRef?.metalness ??
    defaults?.metalness ??
    0;
  const sheen = resolvedSurfaceRef?.sheen ?? surfaceRef?.sheen ?? defaults?.sheen;
  const sheenRoughness =
    resolvedSurfaceRef?.sheenRoughness ??
    surfaceRef?.sheenRoughness ??
    defaults?.sheenRoughness ??
    0.5;
  const sheenColor =
    resolvedSurfaceRef?.sheenColor ??
    surfaceRef?.sheenColor ??
    defaults?.sheenColor ??
    0xffffff;
  const useClothSheen = typeof sheen === "number" && sheen > 0;
  const catalogFallbackColor = surfaceRef?.id
    ? getBuiltInFurnitureMaterial(surfaceRef.id)?.color
    : undefined;

  const replaceMap = React.useCallback((nextMap: THREE.Texture | null) => {
    if (mapRef.current) {
      releaseCatalogTextureVariant(mapRef.current);
      mapRef.current = null;
    }
    mapRef.current = nextMap;
    setMap(nextMap);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    // Drop the previous albedo immediately so limestone/oak do not linger while
    // the next texture loads (or when switching to procedural glass / no map).
    replaceMap(null);
    invalidate();

    if (!imageUrl) {
      return () => {
        cancelled = true;
      };
    }

    acquireCatalogTextureVariant(imageUrl, gl, {
      repeatScale,
      ...(repeatScaleY !== undefined ? { repeatScaleY } : {}),
      rotation,
      uvChannel,
      ...(maxTextureSize !== undefined ? { maxTextureSize } : {}),
      ...(forceTextureWidth ? { forceWidth: true } : {}),
    })
      .then((texture) => {
        if (cancelled) {
          releaseCatalogTextureVariant(texture);
          return;
        }
        replaceMap(texture);
        invalidate();
      })
      .catch((error) => {
        // CORS / missing albedo: keep the solid fallback color instead of
        // crashing the CRA error overlay with an uncaught Image error Event.
        if (cancelled) return;
        const reason =
          error instanceof Error
            ? error.message
            : error instanceof Event
              ? error.type
              : String(error);
        trackFloorplan3DMetric("texture_load_failed", {
          url: imageUrl,
          reason,
        });
        replaceMap(null);
        invalidate();
      });

    return () => {
      cancelled = true;
    };
  }, [
    forceTextureWidth,
    gl,
    imageUrl,
    invalidate,
    maxTextureSize,
    repeatScale,
    repeatScaleY,
    replaceMap,
    rotation,
    uvChannel,
  ]);

  React.useEffect(
    () => () => {
      if (mapRef.current) {
        releaseCatalogTextureVariant(mapRef.current);
        mapRef.current = null;
      }
    },
    [],
  );

  const materialColor = resolveCatalogStandardMaterialColor({
    map,
    imageUrl,
    surfaceRefColor: resolvedSurfaceRef?.color ?? surfaceRef?.color,
    slotColor: color,
    catalogFallbackColor,
  });

  /**
   * Demand-mode R3F: swapping map null↔texture requires a shader recompile.
   * Without needsUpdate, albedo often stays invisible until the 3D scene remounts.
   *
   * Also assign `map` explicitly: R3F skips `undefined` props, so
   * `map={map ?? undefined}` left the previous albedo bound after Remove material.
   */
  React.useEffect(() => {
    const material = materialRef.current;
    if (!material) return;
    material.map = map;
    material.needsUpdate = true;
    invalidate();
  }, [invalidate, map, materialColor, hideTextures, useClothSheen]);

  // Always MeshPhysicalMaterial so fabric↔wood switches update props instead of
  // remounting (physical vs standard) across many stair step faces.
  return (
    <meshPhysicalMaterial
      ref={materialRef}
      attach={attach}
      color={materialColor}
      map={map}
      roughness={roughness}
      metalness={metalness}
      side={side}
      opacity={opacity}
      transparent={transparent}
      depthWrite={depthWrite}
      sheen={useClothSheen ? sheen : 0}
      sheenRoughness={sheenRoughness}
      sheenColor={sheenColor}
    />
  );
});

export default CatalogStandardMaterial;
